import { useMemo, useState } from 'react';

import { Anchor, Badge, Box, Chip, Group, Loader, Pagination, Stack, Text } from '@mantine/core';

import {
  useGetPatientActivityQuery,
  useGetReportForAlertNotesQuery
} from '@/store/slices/api/activityFeedApiSlice';
import { useListTasksQuery } from '@/store/slices/api/tasksApiSlice';
import { getReportName } from '@/utils/constants';
import formatDate, { formatUtcDate } from '@/utils/formatDate';
import { routes } from '@/routes';

import { ActivityFeedFilter, ActivityFeedItem, ActivityFeedType } from '@/types/activityFeed';
import { TaskResponseDto } from '@/types/task';
import { ReportColor, ReportType } from '@/types/transmissionReport';

import PatientTasksList from '../PatientTasksList';

import {
  ACTIVITY_ALL_FILTERS,
  ACTIVITY_NOTE_FILTERS,
  callEventCopy,
  isPatientStatusEvent,
  personName,
  statusEventCopy
} from './mapActivityEvent';
import styles from './PatientActivity.module.scss';
import { clipAlertNote, joinAlertNotes, reportAlertNoteText } from './reportAlertNotes';

type ActivityType = 'call' | 'report' | 'letter' | 'note' | 'status' | 'task';
type FilterValue = 'all' | ActivityType;

interface ActivityEvent {
  id: string;
  type: ActivityType;
  direction?: 'inbound' | 'outbound';
  actor: string;
  detail: string;
  lines?: string[];
  at: string;
  link?: { reportId: number; label: string };
  dotColor?: string;
  badgeColor?: string;
  reportId?: number;
  reportColor?: string;
}

const TYPE_META: Record<ActivityType, { label: string; color: string }> = {
  call: { label: 'Call', color: 'teal' },
  report: { label: 'Report', color: 'plum' },
  letter: { label: 'Letter', color: 'orange' },
  note: { label: 'Note', color: 'plum' },
  status: { label: 'Status', color: 'gray' },
  task: { label: 'Task', color: 'violet' } // unused in feed rows; Tasks filter renders PatientTasksList
};

const PAGE_SIZE = 10;

const FILTERS: { value: FilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'report', label: 'Reports' },
  { value: 'task', label: 'Tasks' },
  { value: 'letter', label: 'Letters' },
  { value: 'call', label: 'Calls' }
];

const FILTER_TO_API: Partial<Record<FilterValue, ActivityFeedFilter[]>> = {
  all: ACTIVITY_ALL_FILTERS,
  report: [
    ActivityFeedFilter.RED,
    ActivityFeedFilter.YELLOW,
    ActivityFeedFilter.REMOTE,
    ActivityFeedFilter.IN_CLINIC
  ],
  letter: [ActivityFeedFilter.POST_GRID_LETTERS],
  call: [ActivityFeedFilter.INBOUND, ActivityFeedFilter.OUTBOUND],
  note: ACTIVITY_NOTE_FILTERS,
  status: [ActivityFeedFilter.PATIENT_STATUS]
};

function reportTone(color?: string): { dot: string; badge: string } {
  if (color === ReportColor.Red) return { dot: 'red', badge: 'red' };
  if (color === ReportColor.Yellow) return { dot: 'yellow', badge: 'yellow' };
  if (color === ReportColor.Green) return { dot: 'teal', badge: 'teal' };
  return { dot: 'gray', badge: 'gray' };
}

function mapFeedItem(item: ActivityFeedItem): ActivityEvent | null {
  const at = item.eventDate;
  const eventType = isPatientStatusEvent(item.eventType)
    ? ActivityFeedType.PATIENT_STATUS
    : item.eventType;
  switch (eventType) {
    case ActivityFeedType.TRANSMISSION: {
      const transmissionType = item.data.transmissionType ?? ReportType.Remote;
      const color = (item.data.color as ReportColor) ?? ReportColor.Green;
      const heartFailure =
        item.data.reportType === 'HEART_FAILURE' || item.data.flagHeartFailure === true;
      const title =
        color === ReportColor.Gray
          ? 'Unclassified Report'
          : getReportName(item.data.deviceType ?? '', transmissionType, color, heartFailure);
      const reportId = Number(item.data.id);
      const tone = reportTone(color);
      const feedNotes = clipAlertNote(item.data.alertNotes);
      const rawTransmitted = item.data.transmissionDate
        ? formatDate(item.data.transmissionDate, 'M/DD/YY, h:mm A')
        : '';
      const transmitted =
        rawTransmitted && !rawTransmitted.endsWith('12:00 AM')
          ? rawTransmitted
          : formatDate(at, 'M/DD/YY, h:mm A') || 'Unassigned';
      const service = item.data.dateOfService
        ? formatUtcDate(item.data.dateOfService, 'M/DD/YY')
        : 'Unassigned';
      return {
        id: item.eventId,
        type: 'report',
        actor: title,
        detail: `Transmitted on ${transmitted}`,
        lines: [`Date of Service: ${service}`, ...(feedNotes ? [`Alert notes: ${feedNotes}`] : [])],
        at,
        dotColor: tone.dot,
        badgeColor: tone.badge,
        reportId: Number.isFinite(reportId) ? reportId : undefined,
        reportColor: color,
        link: Number.isFinite(reportId) ? { reportId, label: title } : undefined
      };
    }
    case ActivityFeedType.POSTGRID: {
      const letter = item.data as { type?: string; description?: string; status?: string };
      return {
        id: item.eventId,
        type: 'letter',
        direction: 'outbound',
        actor: personName(item.data.lastUpdatedBy, 'Automated System'),
        detail: `${letter.type ?? letter.description ?? 'Letter'} · ${letter.status ?? 'sent'}`,
        at
      };
    }
    case ActivityFeedType.CLINICAL_NOTE:
      return {
        id: item.eventId,
        type: 'note',
        actor: personName(item.data.lastUpdatedBy),
        detail: item.data.truncatedNoteText ?? 'Clinical note',
        at
      };
    case ActivityFeedType.PATIENT_STATUS: {
      const status = statusEventCopy(item.data);
      return {
        id: item.eventId,
        type: 'status',
        actor: status.actor,
        detail: status.detail,
        lines: status.lines,
        at
      };
    }
    case ActivityFeedType.REGAL_CALL:
    case ActivityFeedType.RETELL_CALLS: {
      const call = callEventCopy(item);
      return {
        id: item.eventId,
        type: 'call',
        direction: call.direction,
        actor: call.actor,
        detail: call.detail,
        lines: call.lines,
        at
      };
    }
    default:
      return null;
  }
}

function alertNotesByReport(tasks: TaskResponseDto[]): Map<string, string> {
  const notes = new Map<string, string>();
  for (const task of tasks) {
    const reportId = task.transmissionReportId;
    const text = clipAlertNote(task.alertNotes);
    if (!reportId || !text) continue;
    const key = String(reportId);
    notes.set(key, joinAlertNotes(notes.get(key), text) ?? text);
  }
  return notes;
}

function ReportAlertNotes({
  reportId,
  color,
  taskNotes
}: {
  reportId: number;
  color?: string;
  taskNotes?: string;
}) {
  const isAlert = color === ReportColor.Red || color === ReportColor.Yellow;
  const { data } = useGetReportForAlertNotesQuery(reportId, {
    skip: !isAlert || Boolean(taskNotes)
  });
  const text = clipAlertNote(
    joinAlertNotes(taskNotes, data ? reportAlertNoteText(data.transmissionReport) : null)
  );
  if (!text) return null;
  return (
    <Text fz="sm" c="plum.8" style={{ whiteSpace: 'pre-wrap' }}>
      Alert notes: {text}
    </Text>
  );
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

const PatientActivity = ({ patientId, hideTasks = false }: { patientId: string; hideTasks?: boolean }) => {
  const [filter, setFilter] = useState<FilterValue>('all');
  const [page, setPage] = useState(1);
  const isTaskFilter = filter === 'task';
  const apiFilters = FILTER_TO_API[filter];
  const { data, isLoading, isError, isFetching } = useGetPatientActivityQuery(
    { patientId, page: page - 1, pageSize: PAGE_SIZE, filters: apiFilters },
    { skip: isTaskFilter }
  );
  const showReportNotes = filter === 'all' || filter === 'report';
  const { data: taskData } = useListTasksQuery(
    { patientId, limit: 100, skip: 0 },
    { skip: !showReportNotes }
  );
  const notesByReport = useMemo(() => alertNotesByReport(taskData?.items ?? []), [taskData?.items]);

  const events = (data?.data ?? [])
    .map(mapFeedItem)
    .filter((event): event is ActivityEvent => event !== null);
  const totalPages = Math.max(1, Math.ceil((data?.pagination?.count ?? 0) / PAGE_SIZE));

  return (
    <Box className={styles.panel}>
      <Chip.Group
        multiple={false}
        value={filter}
        onChange={(value) => {
          setFilter((value || 'all') as FilterValue);
          setPage(1);
        }}>
        <Group gap={6} className={styles.filters}>
          {FILTERS.filter((item) => !(hideTasks && item.value === 'task')).map((item) => (
            <Chip key={item.value} value={item.value} size="xs" radius="sm" variant="outline">
              {item.label}
            </Chip>
          ))}
        </Group>
      </Chip.Group>

      <Box className={styles.feed}>
        {isTaskFilter ? (
          <PatientTasksList patientId={patientId} />
        ) : isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : isError ? (
          <Text className={styles.empty} ta="center" py="xl">
            Could not load activity.
          </Text>
        ) : events.length === 0 ? (
          <Text className={styles.empty} ta="center" py="xl">
            No activity for this filter
          </Text>
        ) : (
          <Stack gap={0} className={styles.timeline}>
            {isFetching && (
              <Group justify="flex-end" mb={6}>
                <Loader size="xs" />
              </Group>
            )}
            {events.map((event) => {
              const meta = TYPE_META[event.type];
              return (
                <div key={event.id} className={styles.row}>
                  <div className={styles.dotCol}>
                    <span className={styles.dot} data-color={event.dotColor ?? meta.color} />
                  </div>
                  <div className={styles.body}>
                    <Group gap={8} align="center" wrap="wrap" mb={2}>
                      <Badge
                        color={event.badgeColor ?? meta.color}
                        variant="light"
                        radius="sm"
                        size="sm"
                        tt="none">
                        {meta.label}
                        {event.direction
                          ? ` · ${event.direction === 'inbound' ? 'In' : 'Out'}`
                          : ''}
                      </Badge>
                      <Text fw={600} fz="sm" c="plum.9">
                        {event.actor}
                      </Text>
                      <Text fz="xs" c="dimmed">
                        {formatWhen(event.at)}
                      </Text>
                    </Group>
                    <Text fz="sm" c="plum.8">
                      {event.detail}
                    </Text>
                    {event.lines?.map((line, index) => (
                      <Text
                        key={`${event.id}-line-${index}`}
                        fz="sm"
                        c="plum.8"
                        style={{ whiteSpace: 'pre-wrap' }}>
                        {line}
                      </Text>
                    ))}
                    {event.reportId != null &&
                      !event.lines?.some((line) => line.startsWith('Alert notes:')) && (
                        <ReportAlertNotes
                          reportId={event.reportId}
                          color={event.reportColor}
                          taskNotes={notesByReport.get(String(event.reportId))}
                        />
                      )}
                    {event.link && (
                      <Anchor
                        href={routes.physician.transmissionReportDetail(
                          String(event.link.reportId)
                        )}
                        target="_blank"
                        fz="xs"
                        fw={600}>
                        View report →
                      </Anchor>
                    )}
                  </div>
                </div>
              );
            })}
          </Stack>
        )}
      </Box>

      {!isTaskFilter && totalPages > 1 && (
        <Group justify="flex-end" className={styles.pager}>
          <Pagination size="sm" total={totalPages} value={page} onChange={setPage} color="plum" />
        </Group>
      )}
    </Box>
  );
};

export default PatientActivity;
